import { createContext, useState, useMemo, useCallback, useEffect, useRef } from 'react'

const CartContext = createContext()

const MAX_PRODUCT_QUANTITY = 20

function loadCart() {
    try {
        const stored = localStorage.getItem('cart')
        return stored ? JSON.parse(stored) : []
    } catch {
        return []
    }
}

function saveCart(items) {
    localStorage.setItem('cart', JSON.stringify(items))
}

export function CartProvider({ collections, children }) {
    const [cartItems, setCartItems] = useState(loadCart)

    // Build variant lookup from retailer collections
    const variantMap = useMemo(() => {
        const map = {}
        for (const collection of collections ?? []) {
            for (const product of collection.products) {
                for (const variant of product.variants) {
                    map[variant.id] = variant
                }
            }
        }
        return map
    }, [collections])

    // Reconcile cart with current stock data
    const lastCollectionsRef = useRef(null)
    useEffect(() => {
        if (!collections || collections === lastCollectionsRef.current) return
        lastCollectionsRef.current = collections

        setCartItems((prev) => {
            const next = prev
                .filter((item) => {
                    const v = variantMap[item.variantId]
                    return v && v.stock_status !== 'unavailable'
                })
                .map((item) => {
                    const v = variantMap[item.variantId]
                    if (v.stock_status === 'low' && v.stock_quantity != null && item.quantity > v.stock_quantity) {
                        return { ...item, quantity: Math.max(1, v.stock_quantity) }
                    }
                    return item
                })
            if (next.length !== prev.length || next.some((item, i) => item !== prev[i])) {
                saveCart(next)
                return next
            }
            return prev
        })
    }, [collections, variantMap])

    const updateItems = (updater) => {
        setCartItems((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater
            saveCart(next)
            return next
        })
    }

    const getProductQuantity = useCallback((productTitle) => {
        return cartItems
            .filter((i) => i.productTitle === productTitle)
            .reduce((sum, i) => sum + i.quantity, 0)
    }, [cartItems])

    const canAddToProduct = useCallback((productTitle, extra = 1) => {
        return getProductQuantity(productTitle) + extra <= MAX_PRODUCT_QUANTITY
    }, [getProductQuantity])

    const addToCart = (item) => {
        updateItems((prev) => {
            const existing = prev.find((i) => i.variantId === item.variantId)
            const productTotal = prev
                .filter((i) => i.productTitle === item.productTitle)
                .reduce((sum, i) => sum + i.quantity, 0)
            const qty = item.quantity ?? 1
            if (existing) {
                const newQty = Math.min(existing.quantity + qty, existing.quantity + (MAX_PRODUCT_QUANTITY - productTotal))
                if (newQty <= existing.quantity) return prev
                return prev.map((i) =>
                    i.variantId === item.variantId
                        ? { ...i, quantity: newQty }
                        : i
                )
            }
            const allowed = Math.min(qty, MAX_PRODUCT_QUANTITY - productTotal)
            if (allowed < 1) return prev
            return [...prev, { ...item, quantity: allowed }]
        })
    }

    const removeFromCart = (variantId) => {
        updateItems((prev) => prev.filter((i) => i.variantId !== variantId))
    }

    const updateQuantity = (variantId, quantity) => {
        if (quantity < 1) return
        updateItems((prev) => {
            const item = prev.find((i) => i.variantId === variantId)
            if (!item) return prev
            const productTotal = prev
                .filter((i) => i.productTitle === item.productTitle)
                .reduce((sum, i) => sum + i.quantity, 0)
            const maxAllowed = item.quantity + (MAX_PRODUCT_QUANTITY - productTotal)
            const clamped = Math.min(quantity, maxAllowed)
            if (clamped < 1) return prev
            return prev.map((i) => (i.variantId === variantId ? { ...i, quantity: clamped } : i))
        })
    }

    const clearCart = () => {
        updateItems([])
    }

    const cartCount = useMemo(
        () => cartItems.reduce((sum, i) => sum + i.quantity, 0),
        [cartItems]
    )

    const cartSubtotal = useMemo(
        () => cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0),
        [cartItems]
    )

    return (
        <CartContext.Provider
            value={{ cartItems, cartCount, cartSubtotal, addToCart, removeFromCart, updateQuantity, clearCart, getProductQuantity, canAddToProduct }}
        >
            {children}
        </CartContext.Provider>
    )
}

export default CartContext
